import { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import styled from 'styled-components';

import Column from 'components/common/Column';

import HackleManager from 'utils/hackle';
import { formatBookingStatus } from 'utils/hackle/parameterFormatting';

import { useBookingListHooks } from './hooks/useBookingListHooks';
import BookingHeader from './BookingHeader';
import BookingScheduleSection from './BookingScheduleSection';
import ModalBase from 'components/Modal/ModalBase';
import AddingScheduleModal from './AddingScheduleModal/AddingScheduleModal';
import { useQuery } from '@apollo/client';
import { ProductSchedules } from './graphQL/types';
import {
  GET_SCHEDULE_BY_TERM,
  GET_UNFIXED_SCHEDULE_TICKET_COUNTS,
  GET_UNFIXED_SCHEDULE_TICKET_LIST,
} from './graphQL';
import { MeData } from 'graphQL/Auth/types';
import { useUserInformation } from 'graphQL/Zustand/useUserInformation';
import { BookingTicket, TicketEdge } from 'types/api';
import { useQueryGetDelivery } from 'containers/Delivery/graphQL/hooks/useQueryGetDelivery';
import { useLocation, useSearchParams } from 'react-router-dom';
import { parseQueryString } from 'utils/common';

const Container = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  /* padding: ${props => (props.$isMobile ? '0 0 90px' : '0 20px 90px')}; */
  width: 100%;
  max-width: 1200px;
  min-height: 100vh;

  border-left: 1px solid #dbdcdf;
  border-right: 1px solid #dbdcdf;

  background-color: #fff;

  @media (max-width: 768px) {
    border-left: none;
    border-right: none;
  }
`;

interface BookingItemsFilter {
  hostId: string;
  unscheduled: boolean;
  customerSearchKeyword: string | null;
  statusIn: string[];
}

const BookingListPage = () => {
  const location = useLocation();
  const queryString = location.search;
  const [bookingId, setBookingId] = useState<string | null>(null); // 예약확정 알림톡에서 가지고온 booking_id

  const [needToRefetch, setNeedToRefetch] = useState(false); // 일정 추가 후 리패치 필요 여부
  const [isAddingScheduleModal, setIsAddingScheduleModal] = useState(false);
  const handleAddingScheduleModal = () => {
    setIsAddingScheduleModal(pre => !pre);
  };

  const { ticketFilterInput, bookingInfo } = useBookingListHooks();

  const { width, ref } = useResizeDetector();

  const isMobile = width ? width < 768 : window.innerWidth < 768;

  const myInfo: MeData = useUserInformation(state => state.userInfo);

  const [bookingItemsFilter, setBookingItemsFilter] =
    useState<BookingItemsFilter>({
      hostId: '',
      unscheduled: true,
      customerSearchKeyword: '',
      statusIn: ['APPLIED'],
    });

  const { data, refetch: refetchUnfixedTicketCount } = useQuery<BookingTicket>(
    GET_UNFIXED_SCHEDULE_TICKET_COUNTS,
    {
      variables: {
        page: 1,
        size: 100,
        filter: bookingItemsFilter,
      },
      skip: !bookingItemsFilter.hostId,
    },
  );

  const {
    isLoading: isDeliveryLoading,
    deliveryData,
    refetch: deliveryRefetch,
  } = useQueryGetDelivery();

  const hasDeliveryItem = deliveryData && deliveryData?.totalCount > 0;

  const totalUnFixedTicketCount = data?.booking?.tickets.totalCount;

  const initRefetchStatus = () => {
    setNeedToRefetch(pre => !pre);
  };

  const initBookingId = () => {
    setBookingId(null);
  };

  useEffect(() => {
    if (bookingInfo) {
      HackleManager.logBookingView(
        bookingInfo.totalCount,
        formatBookingStatus(ticketFilterInput.filter.statusIn),
      );
    }
  }, [bookingInfo]);

  useEffect(() => {
    /* init 함수 */
    if (myInfo) {
      setBookingItemsFilter(pre => ({
        ...pre,
        hostId: myInfo.me.host.id,
      }));
    }
  }, [myInfo]);

  useEffect(() => {
    if (queryString) {
      const queryParams = parseQueryString(queryString);

      if (queryParams && queryParams.booking_id) {
        setBookingId(queryParams.booking_id);

        handleAddingScheduleModal();
      }
    }
  }, [queryString]);

  return (
    <Container ref={ref} $isMobile={isMobile}>
      <Column
        style={{
          padding: isMobile ? '0 0 90px' : '0 20px 90px',
        }}
      >
        <BookingHeader
          handleAddingScheduleModal={handleAddingScheduleModal}
          totalUnFixedTicketCount={totalUnFixedTicketCount}
          hasDeliveryItem={hasDeliveryItem}
          initBookingId={initBookingId}
        />

        <BookingScheduleSection
          needToRefetch={needToRefetch}
          initRefetchStatus={initRefetchStatus}
          hasDeliveryItem={hasDeliveryItem}
          refetchUnfixedTicketCount={refetchUnfixedTicketCount}
        />
      </Column>

      {isAddingScheduleModal && (
        <AddingScheduleModal
          isMobile={isMobile}
          handleModal={handleAddingScheduleModal}
          totalUnFixedTicketCount={totalUnFixedTicketCount}
          hostId={myInfo?.me.host.id}
          initRefetchStatus={initRefetchStatus}
          bookingTicketsRefetch={refetchUnfixedTicketCount}
          bookingId={bookingId}
        />
      )}
    </Container>
  );
};

export default BookingListPage;
